import { pluck } from 'underscore'
import URI from 'urijs'
import { downloadPlaceholders, placeholderUrls } from './multiple_image_helpers.js'
import { presentationEvents } from './presentation_events'
import { prependDoNotModifyToTitle, prependDoNotModifyToTitles } from './util/prepend_dont_modify_to_title'

const DEFAULT_SLIDE_DIMENSION = -1
const DEFAULT_SLIDE_INSERTABLE = true

// If the user is doing a big action, say inserting 400 slides,
// it causes a timeout somewhere due to the number of inserts
// that need to occur. Batching the slides fixes this issue.
const BATCH_SIZE = 10

let instance

function setBindingReferences () {
  this.presentation = window.presentation || window.macWebApp || {}
  this.webApp = window.webApp || window.macWebApp || {}
  this.presentationEvents = presentationEvents
  window.presentationEvents = presentationEvents
}
export default class SlidewareBindings {
  constructor () {
    // Return the singleton if it exists, otherwise create the instance
    if (!instance) {
      instance = this

      setBindingReferences.call(this)
      window.addEventListener('bindingsready', setBindingReferences.bind(this))
    }

    this.operationProgressEvent = 'operationProgress'

    return instance
  }

  /**
   * Listen to events from the slideware layer
   *
   * @param {string}   event        The name of the event which to listen on
   * @param {function} callback     A callback to call when the event is fired
   */
  on (event, callback) {
    if (!this.presentationEvents.subscribe) {
      return new Error('Presentation events are not supported on this platform')
    }
    this.presentationEvents.subscribe(event, callback)
  }

  /**
   * Removes all the presentation event subscribers
   *
   */
  stopListening () {
    if (!this.presentationEvents.clear) {
      return new Error('Presentation events are not supported on this platform')
    }
    this.presentationEvents.clear()
  }

  /**
   * Sends event to subscribers
   *
   * @param {string} eventName    Name of the event to send
   * @param {string} message      A message to send to the subscriber
   * @param {object} data         The data to send to the subscriber
   */
  publishEvent (eventName, message, data) {
    if (!this.presentationEvents.publish) {
      return new Error('Presentation events are not supported on this platform')
    }

    this.presentationEvents.publish(eventName, { message, data })
  }

  /**
   * Insert new slides into slideware that represent activities
   *
   * @param {string[]} titles    An array of strings, which are the titles of each activity
   * @param {string[]} urls      An array of strings, which are the PE URLs of each activity
   * @param {string[]} imageUrls An array of strings, which are the placeholder image URLs of each activity
   *
   * @return {Promise} A Promise. It's resolved (with no value) if insertion completes
   *    successfully. It's rejected if inserting slides isn't supported on the platform.
   */
  async insertActivities (titles, urls, imageUrls) {
    const eventMessage = 'insertActivities'
    if (this.presentation.insertImageSlidesByBytes) {
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_ACTIVITIES_PLACEHOLDER_DOWNLOADING', progress: 0, total: urls.length })
      const encodedBytes = await downloadPlaceholders(imageUrls)
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_ACTIVITIES_PLACEHOLDER_INSERTING', progress: 0, total: urls.length })
      const titleCopy = [...titles]
      const urlsCopy = [...urls]
      while (titleCopy.length) {
        await this.presentation.insertImageSlidesByBytes(
          prependDoNotModifyToTitles(titleCopy.splice(0, BATCH_SIZE)),
          urlsCopy.splice(0, BATCH_SIZE),
          encodedBytes.splice(0, BATCH_SIZE)
        )
      }

      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_ACTIVITIES_PLACEHOLDER_COMPLETE', progress: 0, total: urls.length })
      return
    }

    if (this.presentation.insertSlides) {
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_ACTIVITIES_URL_INSERTING', progress: 0, total: urls.length })
      await this.presentation.insertSlides(prependDoNotModifyToTitles(titles), urls, '')
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_ACTIVITIES_URL_COMPLETE', progress: 0, total: urls.length })
      return
    }

    throw new Error('Inserting activites is not supported on this platform')
  }

  /**
   * Insert new slides into slideware that represent screenshots of activities
   *
   * @param {string[]} titles    An array of strings, which are the titles of each activity
   * @param {string[]} urls      An array of strings, which are the PE URLs of each activity screenshot
   *
   * @return {Promise} A Promise. It's resolved (with no value) if insertion completes
   *    successfully. It's rejected if inserting slides isn't supported on the platform.
   */
  async insertImagesByUrl (titles, urls) {
    const eventMessage = 'insertImagesByUrl'
    if (this.presentation.insertScreenshotSlidesByBytes) {
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_IMAGES_DOWNLOADING', progress: 0, total: urls.length })
      const encodedBytes = await downloadPlaceholders(urls)
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_IMAGES_INSERTING', progress: 0, total: urls.length })
      // The native layer expects empty urls as the activity url, otherwise it assumes
      // it's an activity it can activate when on screen. So pass empty urls for screenshots.
      const activityUrls = Array(titles.length).fill('')
      const titleCopy = [...titles]
      while (titleCopy.length) {
        await this.presentation.insertScreenshotSlidesByBytes(
          titleCopy.splice(0, BATCH_SIZE),
          activityUrls.splice(0, BATCH_SIZE),
          encodedBytes.splice(0, BATCH_SIZE)
        )
      }

      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_IMAGES_COMPLETE', total: urls.length })
      return
    }

    if (this.presentation.insertImageSlidesByUrl) {
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_IMAGES_DOWNLOADING', progress: 0, total: urls.length })
      const imageUrls = await placeholderUrls(urls)
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_IMAGES_INSERTING', progress: 0, total: urls.length })
      await this.presentation.insertImageSlidesByUrl(titles, imageUrls)
      this.publishEvent(this.operationProgressEvent, eventMessage, { code: 'INSERT_IMAGES_COMPLETE', total: urls.length })
      return
    }

    throw new Error('Inserting screenshots is not supported on this platform')
  }

  /**
   * Inserts a new slide into the slideware that represent a webpage
   *
   * @param {Object} options - An object that contains information about the webpage
   * @param {string} options.url - URL to load in the presentation
   */
  async insertWebPage (options = { url: '' }) {
    if (!this.presentation || !this.presentation.insertSlideWidget) {
      console.warn(`Inserting a webpage is not supported on this platform. Try to open: ${options.url}`)
      return
    }

    try {
      await this.presentation.insertSlideWidget(options.url, prependDoNotModifyToTitle('LiveSlide'), true, '')
    } catch (e) {
      console.error(`Error insertWebPage: ${e}`)
    }
  }

  /**
   * Get the width of the master slide in the presentation.
   *
   * @return {number} The width of the master slide in the current presentation,
   *    in points. -1 if querying for the width is not supported.
   */
  async masterSlideWidth () {
    if (this.presentation.masterSlideWidth) {
      const width = await this.presentation.masterSlideWidth()
      return width
    }

    return DEFAULT_SLIDE_DIMENSION
  }

  /**
   * Get the height of the master slide in the presentation.
   *
   * @return {number} The height of the master slide in the current presentation,
   *    in points. -1 if querying for the height is not supported.
   */
  async masterSlideHeight () {
    if (this.presentation.masterSlideHeight) {
      const height = await this.presentation.masterSlideHeight()
      return height
    }

    return DEFAULT_SLIDE_DIMENSION
  }

  /**
   * Check whether the slideware can insert a new slide
   *
   * @return {boolean} A boolean indicating whether it's possible to insert a slide into
   *    the presentation at the moment. Will return `true` if the platform doesn't support
   *    checking this state.
   */
  async slidesInsertable () {
    if (this.presentation.slidesInsertable) {
      const insertable = await this.presentation.slidesInsertable()
      return insertable
    } else {
      return DEFAULT_SLIDE_INSERTABLE
    }
  }

  /**
   * Close the native window that the current web app is running in.
   */
  async closeWindow () {
    if (this.webApp.close) {
      return this.webApp.close()
    } else {
      console.warn('Closing the web app window is not supported on this platform')
      throw new Error('Closing the web app window is not supported on this platform')
    }
  }

  /**
   * Get all PE activity URLs represented in slides in the current presentation
   *
   * @return {Promise<URI[], Error>} A Promise that is resolved with an array of URI objects,
   *    which are the URLs of each PE activity in the presentation. This method does not remove
   *    duplicates; if the same activity is in the presentation multiple times, it will be in
   *    the returned array multiple times as well. The promise is rejected if there is a problem
   *    getting URLs or if this check isn't supported by the platform.
   */
  async allActivityUrls () {
    if (!this.presentation.allActivityUrls) {
      throw new Error('Fetching activity URLs is not supported on this platform')
    }

    const activitiesJSON = await this.presentation.allActivityUrls()
    if (!activitiesJSON) {
      throw new Error('Activity urls received are invalid')
    }

    return JSON.parse(activitiesJSON)
      .activityUrls
      .filter(activity => activity.length > 0)
      .map(activity => new URI(activity))
  }

  /**
   * Updates placeholders for slides.
   *
   * @param {Object[]} slideDataObjects An array of Objects, where each entry represents
   *    an activity whose placeholder slides you'd like to update. See other params for
   *    what info is needed to properly update placeholders.
   * @param {string} slideDataObjects.activityUrl A string with the PE URL of the activity
   *    to update.
   * @param {string} slideDataObjects.placeholderUrl A string with the placeholder URL of
   *    the activity to update.
   *
   * @return {Promise} A Promise. It's resolved (with no value) if updates complete
   *    successfully. It's rejected if updating placeholders isn't supported on the platform, or
   *    no inputs are given.
   */
  async updateAllSlides (slideDataObjects) {
    if (!slideDataObjects || !slideDataObjects.length) {
      console.log('There are no slides to update')
      return
    }

    if (!this.presentation.updatePlaceholdersByBytes) {
      throw new Error('Updating placeholders is not supported on this platform')
    }

    const encodedBytes = await downloadPlaceholders(pluck(slideDataObjects, 'placeholderUrl'))
    const urls = pluck(slideDataObjects, 'activityUrl')
    while (urls.length) {
      await this.presentation.updatePlaceholdersByBytes(
        urls.splice(0, BATCH_SIZE),
        encodedBytes.splice(0, BATCH_SIZE)
      )
    }
  }

  /**
   * Notify the platform that a user has logged into the web app.
   *
   * The platform can do whatever it deems appropriate with this knowledge.
   * Usually this information is used to enable UI elements that are only applicable
   * with a logged in user.
   *
   * @param {string} username The name of the user who just logged in
   */
  async login (username) {
    if (this.presentation.login) {
      await this.presentation.login(username)
      return
    }

    console.warn('Logging in is not supported on this platform')
  }

  /**
   * Notify the platform that a user has logged out of the web app.
   *
   * The platform can do whatever it deems appropriate with this knowledge.
   * Usually this information is used to disable controls that don't make sense
   * without a logged in user.
   */
  async logout () {
    if (this.presentation.logout) {
      await this.presentation.logout()
      return
    }

    console.warn('Logging out is not supported on this platform')
  }

  /**
   * Notify the platform that its configuration should change. (presenter url, features enabled, etc.)
   *
   *  @param {Object} config    An object, created from config_app_util.js appConfig(...).
   *    Contains information that decides how the app should be configured.
   */
  async configApp (config = {}) {
    if (!this.presentation.configApp) {
      console.warn('Changing application configuration is not supported on this platform')
      return
    }

    try {
      await this.presentation.configApp(JSON.stringify(config))
    } catch (e) {
      console.warn(`Error JSON.stringify: ${e}`)
    }
  }

  /**
   * Check whether the slideware is capable of sending app logs to PE support in an automated
   * fashion
   *
   * @return {boolean} A boolean indicating whether the platform is capable of emailing the
   *    logs to our PE support address
   */
  canSendAppLogs () {
    return Boolean(this.presentation.sendAppLogs)
  }

  /**
   * Notify the platform that a user has requested to send their platform logs
   *
   * @param {Object} json - An object that represents the user information being sent to Zendesk.
   * @param {string} json.ticketId - the ticket id assigned by a Zendesk response
   * @param {string} json.email - the user's email
   *
   * The platform can do whatever it deems appropriate with this knowledge.
   * On some, it will send a message to AWS with the logs
   */
  async sendAppLogs (json) {
    if (this.presentation.sendAppLogs) {
      try {
        await this.presentation.sendAppLogs(JSON.stringify(json))
      } catch (e) {
        console.warn(`Error sendAppLogs: ${e}`)
      }
    } else {
      console.warn('Sending logs is not supported on this platform')
    }
  }

  canOpenBrowser () {
    return !!this.webApp.openBrowser
  }

  /**
   * Notify the platform that it should open an external browser window.
   *
   * @param {Object} options - An object that contains information about the url that was clicked.
   * @param {string} options.url - URL to open in the external browser.
   * @param {Object} options.clickEvent - jQuery click event
   * @param {bool} options.preventDefault - whether or not the click event should be prevented
   *
   * @return {Promise<Error>} A promise that is resolved with nothing if the platform is
   * notified to open the browser. The promise is rejected if there is a problem with the url or
   * if this check isn't supported by the platform.
   */
  async openBrowser (options = { url: '', clickEvent: null, preventDefault: false }) {
    if (options.preventDefault && options.clickEvent) {
      options.clickEvent.preventDefault()
    }

    if (!this.webApp.openBrowser) {
      throw new Error('Opening an external browser is not supported on this platform')
    }

    if (!options.url) {
      throw new Error('The url is empty')
    }

    this.webApp.openBrowser(options.url)
  }

  /**
   * Get the name of the current presentation
   *
   * @return {Promise<string, Error>} A promise. It's resolved with the name of
   * the presentation as a string. This string may include the file extension (e.g.
   * "FooBar.pptx"). The promise is rejected if there's an issue getting the
   * presentation name.
   */
  async fileName () {
    if (!this.presentation.fileName) {
      throw new Error('Getting the presentation name is not supported on this platform')
    }

    return this.presentation.fileName()
  }

  async settings () {
    if (!this.presentation.settings) {
      throw new Error('Getting the slideware app settings is not supported on this platform')
    }

    return JSON.parse(this.presentation.settings())
  }
}
